import axios from 'axios';

// Get API URL with proper production support
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    
    // Production: Use same protocol and host as frontend
    const isHttps = window.location.protocol === 'https:';
    return `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
  }
  
  return 'http://localhost:8000'; // Fallback
};

const API_BASE_URL = getApiUrl() + '/api';
const POLYGON_API_BASE = `${API_BASE_URL}/polygon`;

class AssetService {
  // Get stocks, ETFs, and other assets using Polygon API
  async getAssets(params = {}) {
    try {
      // Support both old (assetClass, market) and new (params object) patterns
      let assetClass, market;
      if (typeof params === 'string') {
        assetClass = params;
        market = arguments[1];
        params = {};
      } else {
        assetClass = params.assetClass;
        market = params.market || 'stocks';
      }
      
      let url = `${POLYGON_API_BASE}/tickers?`;
      
      // Add query parameters
      const queryParams = {
        active: params.active || 'true',
        limit: params.limit || '50',
        sort: params.sort || 'ticker',
        order: params.order || 'asc',
        market: params.market || 'stocks'
      };
      
      // Add type filter if specified
      if (params.type) {
        queryParams.type = params.type;
      } else if (assetClass && assetClass !== 'all') {
        switch(assetClass.toLowerCase()) {
          case 'stocks':
            queryParams.type = 'CS'; // Common Stock
            break;
          case 'etf':
          case 'etfs':
            queryParams.type = 'ETF'; // Exchange Traded Fund
            break;
          case 'bonds':
          case 'bond etf':
            queryParams.type = 'ETF'; // Bond ETFs are still ETFs
            break;
          case 'crypto':
          case 'crypto etf':
            queryParams.type = 'ETF'; // Crypto ETFs
            break;
        }
      }
      
      // Build URL with query string
      url += new URLSearchParams(queryParams).toString();
      
      const response = await axios.get(url);
      return {
        success: true,
        results: response.data.results || [],
        assets: response.data.results || [], // For backward compatibility
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error.response?.data || error;
    }
  }

  // Get popular kid-friendly stocks (Disney, Apple, Netflix, etc.)
  async getKidFriendlyAssets() {
    try {
      // Get specific tickers that kids would recognize
      const kidFriendlyTickers = ['AAPL', 'DIS', 'NFLX', 'TSLA', 'MSFT', 'GOOGL', 'AMZN', 'META'];
      const assets = [];
      
      // Get details for each ticker
      for (const ticker of kidFriendlyTickers) {
        try {
          const response = await axios.get(`${POLYGON_API_BASE}/tickers/${ticker}`);
          if (response.data.status === 'OK') {
            assets.push({
              ticker: response.data.results.ticker,
              name: response.data.results.name,
              description: response.data.results.description,
              logo_url: response.data.results.branding?.logo_url,
              icon_url: response.data.results.branding?.icon_url,
              market_cap: response.data.results.market_cap,
              type: response.data.results.type
            });
          }
        } catch (tickerError) {
          console.warn(`Failed to fetch ${ticker}:`, tickerError);
        }
      }
      
      return {
        success: true,
        assets: assets
      };
    } catch (error) {
      console.error('Error fetching kid-friendly assets:', error);
      throw error.response?.data || error;
    }
  }

  // Get detailed asset information and current price
  async getAssetDetails(ticker) {
    try {
      const [detailsResponse, priceResponse] = await Promise.all([
        axios.get(`${POLYGON_API_BASE}/tickers/${ticker}`),
        axios.get(`${POLYGON_API_BASE}/tickers/${ticker}/prev`)
      ]);
      
      const details = detailsResponse.data.results;
      const priceData = priceResponse.data.results?.[0];
      
      return {
        success: true,
        asset: {
          ticker: details.ticker,
          name: details.name,
          description: details.description,
          logo_url: details.branding?.logo_url,
          icon_url: details.branding?.icon_url,
          market_cap: details.market_cap,
          homepage_url: details.homepage_url,
          total_employees: details.total_employees,
          type: details.type,
          // Current price data
          current_price: priceData?.c, // close price
          day_high: priceData?.h,
          day_low: priceData?.l,
          day_open: priceData?.o,
          volume: priceData?.v,
          volume_weighted_price: priceData?.vw
        }
      };
    } catch (error) {
      console.error('Error fetching asset details:', error);
      throw error.response?.data || error;
    }
  }
  
  // Get asset logo URL (using proxy)
  getAssetLogoUrl(ticker, type = 'logo') {
    return `${POLYGON_API_BASE}/logos/${ticker}/${type}`;
  }
  
  // Legacy method for backwards compatibility
  async getAssetExplanation(ticker) {
    try {
      const details = await this.getAssetDetails(ticker);
      return {
        success: true,
        explanation: details.asset.description,
        details: details.asset
      };
    } catch (error) {
      console.error('Error fetching asset explanation:', error);
      throw error.response?.data || error;
    }
  }
  
  // Get crypto assets
  async getCryptoAssets() {
    try {
      const cryptoTickers = ['BTCUSD', 'ETHUSD', 'ADAUSD', 'SOLUSD'];
      const assets = [];
      
      for (const ticker of cryptoTickers) {
        try {
          const response = await axios.get(`${POLYGON_API_BASE}/crypto/${ticker}/prev`);
          if (response.data.status === 'OK') {
            const priceData = response.data.results[0];
            assets.push({
              ticker: ticker,
              name: ticker.replace('USD', ' / USD'),
              current_price: priceData.c,
              day_high: priceData.h,
              day_low: priceData.l,
              volume: priceData.v,
              type: 'crypto'
            });
          }
        } catch (cryptoError) {
          console.warn(`Failed to fetch crypto ${ticker}:`, cryptoError);
        }
      }
      
      return {
        success: true,
        assets: assets
      };
    } catch (error) {
      console.error('Error fetching crypto assets:', error);
      throw error.response?.data || error;
    }
  }
}

export default new AssetService();